import React, { useState, useEffect } from 'react';
import { Menu, X, Instagram, Mail, Phone, ChevronRight, Activity, Globe } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { NAV_LINKS, scrollToAnchor } from '@/lib/index';
import { IMAGES } from '@/assets/images';
import { springPresets } from '@/lib/motion';
interface LayoutProps {
  children: React.ReactNode;
}
export function Layout({
  children
}: LayoutProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const handleNavClick = (href: string) => {
    setIsMenuOpen(false);
    scrollToAnchor(href);
  };
  return <div className="min-h-screen flex flex-col bg-background selection:bg-primary/20 selection:text-primary">
      {/* Header */}
      <header className={`fixed top-0 w-full z-50 transition-all duration-300 h-20 ${isScrolled ? 'bg-background/80 backdrop-blur-lg border-b border-border shadow-sm' : 'bg-transparent'}`}>
        <div className="container mx-auto h-full px-4 flex items-center justify-between">
          {/* Logo */}
          <a href="https://gamantraining.com/?srsltid=AfmBOooNcBiBMv_jM1UT5oriLsUL8v-jnm37OIyLcp2zXyp_pxb4jXaY" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 group cursor-pointer">
            <div className="w-10 h-10 rounded-xl overflow-hidden transform group-hover:rotate-12 transition-transform p-1">
              <img src={IMAGES.LOGO_NEGRO} alt="Programa Recupera Logo" className="w-full h-full object-contain" />
            </div>
            <span className="text-xl font-bold tracking-tighter text-foreground">
              PROGRAMA <span className="text-primary">RECUPERA</span>
            </span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map(link => <a key={link.href} href={link.href} onClick={e => {
            e.preventDefault();
            handleNavClick(link.href);
          }} className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
                {link.label}
              </a>)}
            <Button onClick={() => window.open('https://8798b9-2.myshopify.com/cart/52472610423125:1?channel=buy_button', '_blank')} className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-6 rounded-full">
              Solo 10€/mes*
            </Button>
          </nav>

          {/* Mobile Toggle */}
          <button className="md:hidden p-2 text-foreground" onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label="Toggle menu">
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation Overlay */}
        <AnimatePresence>
          {isMenuOpen && <motion.div initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={springPresets.snappy} className="absolute top-20 left-0 w-full bg-background border-b border-border shadow-xl md:hidden">
              <nav className="flex flex-col p-6 gap-4">
                {NAV_LINKS.map(link => <a key={link.href} href={link.href} onClick={e => {
              e.preventDefault();
              handleNavClick(link.href);
            }} className="text-lg font-medium text-foreground hover:text-primary transition-colors py-2 border-b border-border/50">
                    {link.label}
                  </a>)}
                <Button onClick={() => handleNavClick('#contacto')} className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold mt-4">
                  Comenzar Transformación
                </Button>
              </nav>
            </motion.div>}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <main className="flex-grow pt-20">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-card border-t border-border">
        <div className="container mx-auto px-4 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            {/* Brand Info */}
            <div className="space-y-6">
              <div className="flex items-center gap-2">
                
                
              </div>
              <p className="text-muted-foreground leading-relaxed">
                Tu viaje de 12 meses hacia una vida sin dolor, con más energía y una movilidad renovada. Todo en la palma de tu mano.
              </p>
              
            </div>

            {/* Quick Links */}
            <div>
              
              <ul className="space-y-4">
                {NAV_LINKS.map(link => <li key={link.href}>
                    <a href={link.href} onClick={e => {
                  e.preventDefault();
                  handleNavClick(link.href);
                }} className="text-muted-foreground hover:text-primary flex items-center gap-2 transition-colors">
                      <ChevronRight size={14} />
                      {link.label}
                    </a>
                  </li>)}
              </ul>
            </div>

            {/* Contact */}
            <div>
              
              <ul className="space-y-4">
                <li className="flex items-start gap-3 text-muted-foreground">
                  <Mail size={18} className="text-primary shrink-0 mt-1" />
                  <span>soporte@gamantraining.com</span>
                </li>
                <li className="flex items-start gap-3 text-muted-foreground">
                  <Globe size={18} className="text-primary shrink-0 mt-1" />
                  <span>www.gamantrainin.com</span>
                </li>
              </ul>
            </div>

            {/* App Promotion */}
            <div className="bg-muted/50 p-6 rounded-2xl border border-border">
              <h4 className="font-bold mb-3 text-foreground">Nuestra App</h4>
              <p className="text-sm text-muted-foreground mb-4">
                Disponible para iOS y Android. Accede a todas tus rutinas y documentos en cualquier lugar.
              </p>
              <div className="flex flex-col gap-2">
                <div className="h-10 bg-black rounded-md flex items-center justify-center text-white text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity" onClick={() => window.open('https://apps.apple.com/es/app/gaman-training/id6467008161', '_blank')}>
                  App Store
                </div>
                <div className="h-10 bg-black rounded-md flex items-center justify-center text-white text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity" onClick={() => window.open('https://play.google.com/store/apps/details?id=io.harbiz.gamantraining&hl=es_419&pli=1', '_blank')}>
                  Google Play
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-16 pt-8 border-t border-border flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground">
              © 2026 Programa Recupera. Todos los derechos reservados.
            </p>
            <div className="flex gap-8 text-sm text-muted-foreground">
              <a href="https://gamantraining.com/policies/legal-notice" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">Aviso Legal</a>
              <a href="https://gamantraining.com/policies/privacy-policy" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">Privacidad</a>
              <a href="https://gamantraining.com/policies/terms-of-service" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">Términos del servicio</a>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}